import {
  IonBadge,
  IonContent,
  IonInfiniteScroll,
  IonModal,
  NavContext
} from '@ionic/react';
import React, { useCallback, useContext, useRef } from 'react';

import { Empty, ModalHeader, Padding, Spacer } from '@/components/common';
import {
  NotificationItem,
  NotificationPlaceholder
} from '@/components/dashboard';
import { useNotifications, useTabs } from '@/contexts';
import { DentistNotification } from '@/data/DentistNotifications';
import {
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_CATEGORY_IDENTIFIERS,
  POPUP_IDENTIFIERS,
  REQUEST_STATUS_OPTIONS
} from '@/utils/constants';
import { parseDate } from '@/utils/helpers/parseDate';
import { ROUTES } from '@/utils/routes';
import { TABS } from '@/utils/tabs';
import { NotificationCategory } from '@/utils/types';
import { format, isToday } from 'date-fns';
import { close, notificationsOutline } from 'ionicons/icons';

import './Notifications.css';

interface NotificationsProps {
  isOpen: boolean;
  onClose: () => void;
  presentingElement?: HTMLElement | undefined;
}

const Notifications: React.FC<NotificationsProps> = ({
  isOpen,
  onClose,
  presentingElement
}) => {
  const { navigate } = useContext(NavContext);
  const {
    notifications,
    groupedNotifications,
    loading,
    loaded,
    fetchMoreNotifications,
    loadingMore,
    hasMore
  } = useNotifications();
  const { setSelectedTab } = useTabs();

  const modal = useRef<HTMLIonModalElement>(null);

  const loadMore = async (event: CustomEvent<void>) => {
    const infiniteScroll = event.target as HTMLIonInfiniteScrollElement;

    if (hasMore && !loadingMore) {
      await fetchMoreNotifications();
    }

    await infiniteScroll.complete();
  };

  const onNotificationClick = useCallback(
    (
      notification: DentistNotification,
      notificationCategory: NotificationCategory
    ) => {
      if (notificationCategory === NOTIFICATION_CATEGORIES.WEEKLY_REPORT) {
        navigate(`${ROUTES.PROFILE}?popup=${POPUP_IDENTIFIERS.WEEKLY_REPORT}`);
        setSelectedTab(TABS.PROFILE);
        onClose();
        return;
      }

      if (notification.request) {
        const request = notification.request;
        if (
          notificationCategory === NOTIFICATION_CATEGORIES.REQUEST ||
          request.status === REQUEST_STATUS_OPTIONS.CANCELLED
        ) {
          navigate(
            `${ROUTES.REQUESTS}?query=${encodeURIComponent(
              request?.patient?.first_name || ''
            )} ${encodeURIComponent(request?.patient?.last_name || '')}&status=${
              request.status
            }&focusedRequestId=${request.id}`
          );
          setSelectedTab(TABS.REQUESTS);
          onClose();
          return;
        }
        navigate(
          `${ROUTES.CALENDAR}?selectedDate=${request.date}&focusedRequestId=${request.id}`
        );
        setSelectedTab(TABS.CALENDAR);
        onClose();
      }
    },
    [navigate, setSelectedTab, onClose]
  );

  const getNotificationCategory = (message: string) => {
    if (message.includes(NOTIFICATION_CATEGORY_IDENTIFIERS.REMINDER)) {
      return NOTIFICATION_CATEGORIES.REMINDER;
    } else if (message.includes(NOTIFICATION_CATEGORY_IDENTIFIERS.CANCELLED)) {
      return NOTIFICATION_CATEGORIES.CANCELLED;
    } else if (
      message.includes(NOTIFICATION_CATEGORY_IDENTIFIERS.RESCHEDULED)
    ) {
      return NOTIFICATION_CATEGORIES.RESCHEDULED;
    } else if (
      message.includes(NOTIFICATION_CATEGORY_IDENTIFIERS.WEEKLY_REPORT)
    ) {
      return NOTIFICATION_CATEGORIES.WEEKLY_REPORT;
    }
    return NOTIFICATION_CATEGORIES.REQUEST;
  };

  const renderNotificationGroups = () => {
    const groups = [];

    if (groupedNotifications.unread.length > 0) {
      groups.push(
        <div key="unread" className="notification-list__section">
          <h3 className="notification-list__title">
            Unread{' '}
            <IonBadge color="danger">
              {groupedNotifications.unread.length}
            </IonBadge>
          </h3>
          <div className="notification-list__items">
            {groupedNotifications.unread.map((notification) => {
              const notificationCategory = getNotificationCategory(
                notification.message
              );

              return (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  notificationCategory={notificationCategory}
                  isUnread
                  onClick={onNotificationClick}
                />
              );
            })}
          </div>
        </div>
      );
    }

    Object.keys(groupedNotifications.readByDate).forEach((date) => {
      groups.push(
        <div key={date} className="notification-list__section">
          <h3 className="notification-list__title">
            {isToday(parseDate(date))
              ? 'Today'
              : format(parseDate(date), 'MMMM d, yyyy')}
          </h3>
          <div className="notification-list__items">
            {groupedNotifications.readByDate[date].map((notification) => {
              const notificationCategory = getNotificationCategory(
                notification.message
              );

              return (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  notificationCategory={notificationCategory}
                  isUnread={false}
                  onClick={onNotificationClick}
                />
              );
            })}
          </div>
        </div>
      );
    });

    return groups;
  };

  const renderNotificationsSkeleton = (count: number = 5) => {
    return (
      <div className="notification-list">
        <div className="notification-list__section">
          <div className="notification-list__items">
            {Array.from({ length: count }).map((_, index) => (
              <NotificationPlaceholder key={index} />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderNotifications = () => {
    if (loading || !loaded) {
      return (
        <>
          <Spacer top={24} />
          {renderNotificationsSkeleton()}
        </>
      );
    }

    if (notifications.length === 0) {
      return (
        <Empty
          icon={notificationsOutline}
          message={
            <>
              When you get notifications,
              <br />
              they'll show up here
            </>
          }
        />
      );
    }

    return (
      <>
        <div className="notification-list">{renderNotificationGroups()}</div>
        <IonInfiniteScroll onIonInfinite={loadMore} disabled={!hasMore}>
          {renderNotificationsSkeleton(2)}
        </IonInfiniteScroll>
      </>
    );
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      presentingElement={presentingElement}
      className="notifications"
      ref={modal}
    >
      <IonContent>
        <Padding withSafeArea={!presentingElement}>
          <ModalHeader
            title="Notifications"
            rightIcon={close}
            onClickRightIcon={() => {
              modal.current?.dismiss();
            }}
            fixed
          />
          {renderNotifications()}
        </Padding>
      </IonContent>
    </IonModal>
  );
};

export default Notifications;
